import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Blog = () => {
  const blogPosts = [
    {
      id: 1,
      title: 'The Importance of Cybersecurity in Today\'s Digital World',
      excerpt: 'In today\'s digital world, cybersecurity is more important than ever. With the increasing use of technology and the growth of online threats, businesses must prioritize security measures to protect their data and operations.',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=600&fit=crop',
      category: 'Cybersecurity',
      date: 'March 15, 2025',
      slug: 'cybersecurity-importance'
    },
    {
      id: 2,
      title: 'System Integration and IT Infrastructure: Enhancing Business Operations',
      excerpt: 'In today\'s fast-paced business world, companies rely heavily on IT infrastructure to support and streamline their operations. System integration plays a crucial role in connecting various systems and applications.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=600&fit=crop',
      category: 'System Integration',
      date: 'March 12, 2025',
      slug: 'system-integration'
    },
    {
      id: 3,
      title: 'Unlocking the Power of Artificial Intelligence and Machine Learning',
      excerpt: 'Artificial Intelligence (AI) and Machine Learning (ML) are two of the most exciting and rapidly developing fields of technology today. They have the potential to transform industries and create new opportunities.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop',
      category: 'Technology',
      date: 'March 10, 2025',
      slug: 'ai-ml-power'
    },
    {
      id: 4,
      title: 'Understanding the Power of Blockchain Technology',
      excerpt: 'Blockchain is a revolutionary technology that is transforming the way we store, share, and access data. It offers unprecedented security, transparency, and decentralization for various applications.',
      image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800&h=600&fit=crop',
      category: 'Blockchain',
      date: 'March 8, 2025',
      slug: 'blockchain-power'
    },
    {
      id: 5,
      title: 'Exploring the Future of the Internet: Web 3.0 and the Metaverse',
      excerpt: 'The internet has come a long way since its inception. From basic text-based pages to rich multimedia experiences, we are now entering the era of Web 3.0 and the Metaverse.',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&h=600&fit=crop',
      category: 'Technology',
      date: 'March 5, 2025',
      slug: 'web3-metaverse'
    },
    {
      id: 6,
      title: 'The Importance of Software Development for Businesses',
      excerpt: 'Software development has become an essential aspect of modern business operations. From automating processes to improving customer experiences, custom software solutions drive efficiency and growth.',
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&h=600&fit=crop',
      category: 'Software Development',
      date: 'March 1, 2025',
      slug: 'software-development'
    }
  ];

  const categories = ['All', 'Cybersecurity', 'System Integration', 'Technology', 'Blockchain', 'Software Development'];
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Blog</h1>
            <p className="text-xl text-blue-100">
              Insights, updates, and expertise on technology, cybersecurity, and digital transformation
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={selectedCategory === category ? 'bg-blue-600' : ''}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-none shadow-lg">
                <img src={post.image} alt={post.title} className="w-full h-56 object-cover" />
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-blue-600">{post.category}</span>
                    <span className="text-sm text-gray-500">{post.date}</span>
                  </div>
                  <CardTitle className="text-xl mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <Link to={`/blog/${post.slug}`}>
                    <Button variant="link" className="px-0 text-blue-600">
                      Read More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Subscribe to Our Newsletter</h2>
            <p className="text-xl text-gray-600 mb-8">
              Get the latest insights and updates delivered directly to your inbox
            </p>
            <div className="flex gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;